import { z } from 'zod';

/**
 * Specify your server-side environment variables schema here. This way you can ensure the app isn't
 * built with invalid env vars.
 */

// Add `.min(1) on ID and SECRET if you want to make sure they're not empty
const server = z.object({
    // NODE_ENV: z.enum(['development', 'test', 'production']),
    // ANALYZE: z
    //     .string()
    //     .toLowerCase()
    //     .transform((x) => x === 'true')
    //     .pipe(z.boolean()),
    APP_NAME: z.enum(['ONE_GATE', 'MOT_CUA', '']),
    ONE_GATE_REQUIRE_LOGIN: z
        .string()
        .transform((val) => val === 'true')
        .optional(),
    /**
     * Ép buộc đăng nhập qua SSO, cấu hình riêng theo từng site (APP_NAME).
     * Khi true: chưa đăng nhập mà truy cập path yêu cầu đăng nhập sẽ tự đẩy sang SSO
     * (truy cập thẳng /signin vẫn mở form); sau khi logout cũng đẩy thẳng sang SSO.
     */
    ONE_GATE_FORCE_SSO_LOGIN: z
        .string()
        .transform((val) => val === 'true')
        .optional(),
    MOT_CUA_FORCE_SSO_LOGIN: z
        .string()
        .transform((val) => val === 'true')
        .optional(),
    /**
     * Loại giao diện đăng nhập: 'default' (layout 2 cột cũ) hoặc 'sso' (giao diện Keycloak theme)
     */
    LOGIN_UI_TYPE: z.enum(['default', 'sso']).optional().default('default'),
    APP_URL: z.string().url(),
    ONE_GATE_URL: z.string().url(),
    MOT_CUA_URL: z.string().url(),
    API_URL: z.string().url(),
    API_KEY: z.string().optional(),
    API_URL_DASHBOARD: z.string().url(),
    API_KEY_DASHBOARD: z.string().optional(),
    API_DEFAULT_VERSION: z.string(),
    API_ABORT_AFTER: z.coerce.number().optional().default(60000),
    USER_DEFAULT_PASSWORD: z.string(),
    /**
     * Enable file logger for the server.
     * This is useful for debugging and development purposes.
     */
    ENABLE_FILE_LOGGER: z
        .string()
        .transform((val) => val === 'true')
        .optional(),

    /**
     * Enable refresh token for authentication.
     * This is useful for keeping users logged in without requiring them to re-authenticate frequently.
     */
    AUTH_ENABLE_REFRESH_TOKEN: z
        .string()
        .transform((val) => val === 'true')
        .optional(),
    AUTH_GOOGLE_ID: z.string().optional(),
    AUTH_GOOGLE_SECRET: z.string().optional(),
    AUTH_MICROSOFT_ENTRA_ID_ID: z.string().optional(),
    AUTH_MICROSOFT_ENTRA_ID_SECRET: z.string().optional(),
    AUTH_MICROSOFT_ENTRA_ID_TENANT_ID: z.string().optional(),
});

/**
 * Specify your client-side environment variables schema here. This way you can ensure the app isn't
 * built with invalid env vars. To expose them to the client, prefix them with `NEXT_PUBLIC_`.
 */
const client = z.object(
    /** @satisfies {Record<`NEXT_PUBLIC_${string}`, import('zod').ZodType>} */({
        NEXT_PUBLIC_APP_URL: z.string(),
        NEXT_PUBLIC_APP_VERSION: z.string(),
        NEXT_PUBLIC_LANGUAGES: z.string(),
        NEXT_PUBLIC_DEFAULT_LANGUAGE: z.string(),
        NEXT_PUBLIC_SSO_CLIENT_ID: z.string(),
        NEXT_PUBLIC_SSO_URL: z.string().url(),
        NEXT_PUBLIC_SSO_REDIRECT_URI: z.string().url(),
        NEXT_PUBLIC_APP_CODE: z.string(),
        /**
         * Mã trường của bản build hiện tại, dạng chuỗi JSON array (vd: "['UEL']"),
         * dùng để cấu hình các hành vi khác nhau theo từng trường.
         */
        NEXT_PUBLIC_SCHOOL_CODE: z.string().optional(),
        NEXT_PUBLIC_SIGNALR_URL: z.string().url(),
        NEXT_PUBLIC_BUSSINESS_POSTAL_FEE: z.coerce
            .number()
            .optional()
            .default(0),
        NEXT_PUBLIC_CAPTCHA_KEY: z.string().optional(),
        /**
         * Mode hiển thị danh sách loại thủ tục ở header/menu khách
         * hàng:
         *  - `public`: hiển thị toàn bộ cây loại thủ tục (mặc định).
         *  - `personalized`: lọc cây theo `ProcedureTypeStartingCodes`
         *    + `RegexBuilderJson` ứng với user đang đăng nhập, gộp
         *    nhánh single-child, auto-redirect `p-types` invalid.
         */
        NEXT_PUBLIC_PROCEDURE_TYPE_MODE: z
            .enum(['public', 'personalized'])
            .optional()
            .default('public'),
    })
);

/**
 * You can't destruct `process.env` as a regular object in the Next.js edge runtimes (e.g.
 * middlewares) or client-side so we need to destruct manually.
 *
 * @type {Record<keyof z.infer<typeof server> | keyof z.infer<typeof client>, string | undefined>}
 */
const processEnv = {
    //
    // NODE_ENV: isDevEnv(),
    // ANALYZE: process.env.ANALYZE,
    APP_NAME: process.env.APP_NAME,
    ONE_GATE_REQUIRE_LOGIN: process.env.ONE_GATE_REQUIRE_LOGIN,
    ONE_GATE_FORCE_SSO_LOGIN: process.env.ONE_GATE_FORCE_SSO_LOGIN,
    MOT_CUA_FORCE_SSO_LOGIN: process.env.MOT_CUA_FORCE_SSO_LOGIN,
    LOGIN_UI_TYPE: process.env.LOGIN_UI_TYPE,
    APP_URL: process.env.APP_URL,
    ONE_GATE_URL: process.env.ONE_GATE_URL,
    MOT_CUA_URL: process.env.MOT_CUA_URL,
    API_URL: process.env.API_URL,
    API_KEY: process.env.API_KEY,
    API_DEFAULT_VERSION: process.env.API_DEFAULT_VERSION,
    API_ABORT_AFTER: process.env.API_ABORT_AFTER,
    USER_DEFAULT_PASSWORD: process.env.USER_DEFAULT_PASSWORD,
    ENABLE_FILE_LOGGER: process.env.ENABLE_FILE_LOGGER,
    API_URL_DASHBOARD: process.env.API_URL_DASHBOARD,
    API_KEY_DASHBOARD: process.env.API_KEY_DASHBOARD,
    //
    AUTH_ENABLE_REFRESH_TOKEN: process.env.AUTH_ENABLE_REFRESH_TOKEN,
    AUTH_GOOGLE_ID: process.env.AUTH_GOOGLE_ID,
    AUTH_GOOGLE_SECRET: process.env.AUTH_GOOGLE_SECRET,
    AUTH_MICROSOFT_ENTRA_ID_ID: process.env.AUTH_MICROSOFT_ENTRA_ID_ID,
    AUTH_MICROSOFT_ENTRA_ID_SECRET: process.env.AUTH_MICROSOFT_ENTRA_ID_SECRET,
    AUTH_MICROSOFT_ENTRA_ID_TENANT_ID:
        process.env.AUTH_MICROSOFT_ENTRA_ID_TENANT_ID,
    //
    NEXT_PUBLIC_APP_URL: process.env.NEXT_PUBLIC_APP_URL,
    NEXT_PUBLIC_APP_VERSION: process.env.NEXT_PUBLIC_APP_VERSION,
    NEXT_PUBLIC_LANGUAGES: process.env.NEXT_PUBLIC_LANGUAGES,
    NEXT_PUBLIC_DEFAULT_LANGUAGE: process.env.NEXT_PUBLIC_DEFAULT_LANGUAGE,
    NEXT_PUBLIC_SSO_CLIENT_ID: process.env.NEXT_PUBLIC_SSO_CLIENT_ID,
    NEXT_PUBLIC_SSO_URL: process.env.NEXT_PUBLIC_SSO_URL,
    NEXT_PUBLIC_SSO_REDIRECT_URI: process.env.NEXT_PUBLIC_SSO_REDIRECT_URI,
    NEXT_PUBLIC_CAPTCHA_KEY: process.env.NEXT_PUBLIC_CAPTCHA_KEY,
    NEXT_PUBLIC_APP_CODE: process.env.NEXT_PUBLIC_APP_CODE,
    NEXT_PUBLIC_SCHOOL_CODE: process.env.NEXT_PUBLIC_SCHOOL_CODE,
    NEXT_PUBLIC_BUSSINESS_POSTAL_FEE:
        process.env.NEXT_PUBLIC_BUSSINESS_POSTAL_FEE,
    NEXT_PUBLIC_SIGNALR_URL: process.env.NEXT_PUBLIC_SIGNALR_URL,
    NEXT_PUBLIC_PROCEDURE_TYPE_MODE: process.env.NEXT_PUBLIC_PROCEDURE_TYPE_MODE,
};

// Don't touch the part below
// --------------------------

const merged = server.merge(client);

/** @typedef {z.input<typeof merged>} MergedInput */
/** @typedef {z.infer<typeof merged>} MergedOutput */
/** @typedef {z.SafeParseReturnType<MergedInput, MergedOutput>} MergedSafeParseReturn */

let env = /** @type {MergedOutput} */ (process.env);

const skip =
    !!process.env.SKIP_ENV_VALIDATION &&
    process.env.SKIP_ENV_VALIDATION !== 'false' &&
    process.env.SKIP_ENV_VALIDATION !== '0';
if (!skip) {
    const isServer = typeof window === 'undefined';

    const parsed = /** @type {MergedSafeParseReturn} */ (
        isServer
            ? merged.safeParse(processEnv) // on server we can validate all env vars
            : client.safeParse(processEnv) // on client we can only validate the ones that are exposed
    );

    if (parsed.success === false) {
        console.error(
            '❌ Invalid environment variables:',
            parsed.error.flatten().fieldErrors
        );
        throw new Error('Invalid environment variables');
    }

    env = new Proxy(parsed.data, {
        get(target, prop) {
            if (typeof prop !== 'string') return undefined;
            // Throw a descriptive error if a server-side env var is accessed on the client
            // Otherwise it would just be returning `undefined` and be annoying to debug
            if (!isServer && !prop.startsWith('NEXT_PUBLIC_'))
                throw new Error(
                    process.env.NODE_ENV !== 'development'
                        ? '❌ Attempted to access a server-side environment variable on the client'
                        : `❌ Attempted to access server-side environment variable '${prop}' on the client`
                );
            return target[/** @type {keyof typeof target} */ (prop)];
        },
    });
}

export { env as processEnv };
